/** @format — Route polyline helpers for tracking map */

export function toLatLng(point) {
  if (!point || point.lat == null || point.lng == null) return null;
  return [point.lat, point.lng];
}

function pointsNear(a, b, km = 8) {
  if (!a || !b) return false;
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h)) <= km;
}

function dedupeConsecutive(points) {
  const out = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || !pointsNear(last, p, 0.5)) out.push(p);
  }
  return out;
}

/**
 * Path is always origin → current (vessel) → destination when all three exist.
 * Matches backend: Jacksonville → Baltimore (solid) → Lagos (dashed).
 */
export function buildPathThroughCurrent(routePoints, origin, currentPosition, destination) {
  const start = toLatLng(origin);
  const end = toLatLng(destination);
  const current = toLatLng(currentPosition);

  if (start && current && end) {
    return dedupeConsecutive([start, current, end]);
  }

  const fromApi = (Array.isArray(routePoints) ? routePoints : []).map(toLatLng).filter(Boolean);
  if (fromApi.length >= 2) return dedupeConsecutive(fromApi);
  if (start && end) return [start, end];
  if (start && current) return [start, current];
  return fromApi;
}

/**
 * Solid = start → current; dashed = current → end.
 */
export function splitRouteAtCurrent(path, currentPosition) {
  if (!path?.length) return { completed: [], remaining: [] };
  const current = toLatLng(currentPosition);
  if (!current) return { completed: path, remaining: [] };

  let splitIdx = path.findIndex((p) => pointsNear(p, current, 25));
  if (splitIdx < 0 && path.length >= 3) {
    splitIdx = 1;
  } else if (splitIdx < 0) {
    splitIdx = 0;
  }

  return {
    completed: path.slice(0, splitIdx + 1),
    remaining: path.slice(splitIdx),
  };
}

export function buildRoutePaths({
  routePoints = [],
  origin = null,
  destination = null,
  currentPosition = null,
  progressPercent = 0,
}) {
  const path = buildPathThroughCurrent(routePoints, origin, currentPosition, destination);
  const current = toLatLng(currentPosition);

  if (path.length < 2) {
    return {
      routeStart: path[0] ?? current,
      routeEnd: path[path.length - 1] ?? current,
      routeProgressEnd: current,
      routeCompletedPath: path,
      routeRemainingPath: [],
      routeProgressPercent: progressPercent,
    };
  }

  const { completed, remaining } = splitRouteAtCurrent(path, currentPosition);

  return {
    routeStart: path[0],
    routeEnd: path[path.length - 1],
    routeProgressEnd: current ?? completed[completed.length - 1],
    routeCompletedPath: completed,
    routeRemainingPath: remaining,
    routeProgressPercent: progressPercent,
  };
}
